import { useRoute } from 'vue-router';

import { useIntentMap } from '@/components/VisionAi/composables/useIntentMap';
import {
  DEFAULT_METRIC_AND_ANALYTICS_PROMPTS,
  DEFAULT_SUMMARY_PROMPTS_BY_INTENT_TYPE,
  SUMMARY_INTENT_TYPES,
  VISION_AI_MOCK_PROMPT_ROUTES,
  VISION_AI_SUMMARY_LAUNCH_TYPE,
} from '@/components/VisionAi/constants';
import { aiSummaryLaunchEvent } from '@/components/VisionAi/utils/mixpanel-events';
import { usePageFilters } from '@/components/VisionAi/utils/request-context';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';

export type VisionPromptConfig = {
  message: string;
  intentTypeOverride?: string;
  buttonIdentifier?: string;
  extra?: Record<string, unknown>;
  label?: string;
};

export function isSummaryPromptMessage(message: string) {
  return Object.values(DEFAULT_SUMMARY_PROMPTS_BY_INTENT_TYPE).includes(message);
}

/**
 * "Mock" routes (e.g. Community Insights, Social Listening overview) use placeholder copy instead of
 * a live summary when the user picks a non–metric prompt.
 */
function isMockPromptForRoute(message: string, routeName: string | undefined) {
  return (
    routeName &&
    VISION_AI_MOCK_PROMPT_ROUTES.includes(routeName) &&
    !DEFAULT_METRIC_AND_ANALYTICS_PROMPTS.includes(message)
  );
}

/**
 * Same behavior as clicking a suggested prompt in {@link PromptButton} (Summarize chips, etc.).
 */
export function useSubmitPromptConfig() {
  const visionAiStore = useVisionAiStore();
  const visionAiMessagesStore = useVisionAiMessagesStore();
  const visionAiConversationsStore = useVisionAiConversationsStore();
  const route = useRoute();
  const { checkIfInvalidThreshold } = useIntentMap();
  const pageFilters = usePageFilters();

  /**
   * Explicit intent from the page (e.g. campaign comment summaries): new chat, then
   * `startConversation` with overrides — does not go through the Summaries tab flow.
   */
  async function submitWithIntentOverride(promptConfig: VisionPromptConfig) {
    visionAiStore.startNewChat();
    await visionAiStore.startConversation({
      message: promptConfig.message,
      intentTypeOverride: promptConfig.intentTypeOverride,
      buttonIdentifier: promptConfig.buttonIdentifier,
      extra: promptConfig.extra,
      filters: pageFilters.value,
    });
    aiSummaryLaunchEvent({
      launchedFrom: VISION_AI_SUMMARY_LAUNCH_TYPE.PROMPT,
      intentType: promptConfig.intentTypeOverride,
      buttonIdentifier: promptConfig.buttonIdentifier,
    });
  }

  /**
   * Default "Summarize …" chips and mock-route prompts: open Summaries, reload summary messages,
   * then either show mock copy (threshold / mock route) or start a real conversation.
   */
  async function submitSummaryTabFlow(promptConfig: VisionPromptConfig, isMockPrompt: boolean) {
    visionAiConversationsStore.selectSummaries();
    visionAiStore.clearLocalChatHistory();
    await visionAiMessagesStore.getMessages({
      intentTypes: SUMMARY_INTENT_TYPES,
    } as Parameters<typeof visionAiMessagesStore.getMessages>[0]);

    if (checkIfInvalidThreshold(visionAiStore.currentIntentType) || isMockPrompt) {
      await visionAiStore.createMockMessages({ isMockPrompt });
    } else {
      await visionAiStore.startConversation({
        message: promptConfig.message,
        filters: pageFilters.value,
      } as Parameters<typeof visionAiStore.startConversation>[0]);
    }
    aiSummaryLaunchEvent({
      launchedFrom: VISION_AI_SUMMARY_LAUNCH_TYPE.PROMPT,
      intentType: visionAiStore.currentIntentType,
      buttonIdentifier: undefined,
    });
  }

  /** Full conversational turn (e.g. metric suggestions): leave Summaries and start a chat. */
  async function submitConversationalTurn(promptConfig: VisionPromptConfig) {
    visionAiConversationsStore.setSummariesSelectedValue(false);
    await visionAiStore.startConversation({
      message: promptConfig.message,
      filters: pageFilters.value,
    } as Parameters<typeof visionAiStore.startConversation>[0]);
  }

  async function submitPromptConfig(promptConfig: VisionPromptConfig) {
    if (promptConfig.intentTypeOverride) {
      await submitWithIntentOverride(promptConfig);
      return;
    }

    const isMockPrompt = isMockPromptForRoute(promptConfig.message, route.name as string);
    if (isSummaryPromptMessage(promptConfig.message) || isMockPrompt) {
      await submitSummaryTabFlow(promptConfig, Boolean(isMockPrompt));
      return;
    }

    await submitConversationalTurn(promptConfig);
  }

  return { submitPromptConfig };
}
